<template>
  <div
    class="gap-column"
    :class="{
      'is-inactive': !active
    }"
  >
    <c-event-teaser
      :type="type"
      :date="gapDate"
      :spawner="true"
      :reservation="reservation"
      class="gap-column__event"
    />

    <div class="gap-column__headings-group">
      <span
        v-if="active"
        class="gap-column__time"
        v-html="$t('headings.time')"
      />

      <span
        class="gap-column__waiting"
        v-html="reservation && active
          ? $t('headings.waiting.reservation')
          : $t('headings.waiting.ticket')"
      />

      <span
        v-if="reservation"
        class="gap-column__price"
        v-html="$t('headings.reservationsOnline')"
      />
      <span
        v-else
        class="gap-column__price"
        v-html="$t('headings.ticketsOnline')"
      />

      <span
        class="gap-column__discount"
        v-html="$t('headings.discount')"
      />

      <span
        v-if="active"
        class="gap-column__tour"
        v-html="$t('headings.tour')"
      />
    </div>

    <transition-group
      name="transition-fade"
      tag="div"
    >
      <div
        v-if="!loading && noData"
        key="no-gaps"
        class="gap-column__content"
      >
        <div class="center">
          <small>{{ $t('noData') }}</small>
        </div>
      </div>
      <div
        v-if="!loading && gapData"
        key="gaps"
        class="gap-column__content"
      >
        <c-gap
          v-for="(gap, index) in gapData.gaps"
          :id="type + '-' + gap.gap"
          :key="index"
          :gap="gap"
          :gapdate="gapDate"
          :type="type"
          :reservation="reservation"
          :is-first="index === earliestGapIndex"
          :islasttwo="lastTwo(gap, gapDate, index)"
          :islastfour="lastFour(gap, gapDate, index)"
          :islast="index === gapData.gaps.length - 1"
        />
      </div>
    </transition-group>

    <transition name="transition-fade">
      <div
        v-if="loading"
        class="gap-column__loading"
      >
        <c-loader-spinner :size="1.5" />
      </div>
    </transition>
  </div>
</template>

<script>
import CEventTeaser from './eventTeaser.vue'
import CGap from './gap.vue'
import CLoaderSpinner from './loaderSpinner.vue'

import eventBus from '../eventBus.js'

export default {
  name: 'CGapColumn',

  components: {
    CEventTeaser,
    CGap,
    CLoaderSpinner
  },

  props: {
    type: {
      type: String,
      required: true
    },
    reservation: {
      type: Boolean
    }
  },

  data () {
    return {
      loading: false,
      noData: false
    }
  },

  computed: {
    active () {
      return this.type === 'active'
    },
    gapDate () {
      return this.$store.state.arrivalTable[this.type].date
    },
    gapData () {
      return this.$store.state.gaps[this.gapDate]
    },
    earliestGapIndex () {
      return (this.gapData.earliestGap - 1) / 2
    }
  },

  watch: {
    gapDate () {
      this.loadData()
    }
  },

  created () {
    this.loadData()
  },

  methods: {
    async loadData () {
      this.loading = true
      this.noData = true

      try {
        const requests = [this.$store.dispatch('getGaps', { date: this.gapDate })]

        if (this.active) {
          requests.push(this.$store.dispatch('getTours', { date: this.gapDate }))
        }

        await Promise.all(requests)
        this.noData = false
      } catch (error) {
        eventBus.emit('handleError', error.response.data.error)
        this.noData = true
      } finally {
        this.loading = false
      }
    },
    lastTwo (aGap, gapDate, index) {
      if (aGap.is_open === true) {
        if (
          (
            index + 1 in this.$store.state.gaps[gapDate].gaps &&
            this.$store.state.gaps[gapDate].gaps[index + 1].is_open === false
          ) || (
            index + 2 in this.$store.state.gaps[gapDate].gaps &&
            this.$store.state.gaps[gapDate].gaps[index + 2].is_open === false
          )
        ) {
          return true
        }
      }

      return false
    },
    lastFour (aGap, gapDate, index) {
      if (!this.lastTwo(aGap, gapDate, index)) {
        if (aGap.is_open === true) {
          if (
            (
              index + 3 in this.$store.state.gaps[gapDate].gaps &&
              this.$store.state.gaps[gapDate].gaps[index + 3].is_open === false
            ) || (
              index + 4 in this.$store.state.gaps[gapDate].gaps &&
              this.$store.state.gaps[gapDate].gaps[index + 4].is_open === false
            )
          ) {
            return true
          }
        }
      }
      return false
    }
  }
}
</script>

<i18n locale="en">
{
  "navigation": {
    "prev": "Previous Day",
    "next": "Next Day"
  },
  "hour": "h",
  "headings": {
    "time": "time-slot for admission",
    "waiting": {
      "short": "Waiting Time",
      "ticket": "tickets at the desk<br>with waiting time",
      "reservation": "waiting time without reservation"
    },
    "ticketsOnline": "onlinetickets<br>without waiting time",
    "reservationsOnline": "reservation<br>without waiting time",
    "discount": "online discount",
    "tour": "experience + ticket<br>without waiting time"
  },
  "noData": "No data available"
}
</i18n>

<i18n locale="de">
{
  "navigation": {
    "prev": "Vorheriger Tag",
    "next": "Nächster Tag"
  },
  "hour": "Uhr",
  "headings": {
    "time": "Ankunfts­zeit",
    "waiting": {
      "short": "Wartezeit",
      "ticket": "Tickets vor Ort<br>mit Wartezeit",
      "reservation": "Wartezeit<br>ohne Reservierung"
    },
    "ticketsOnline": "Online­tickets<br>ohne Wartezeit",
    "reservationsOnline": "Reservierung<br>ohne Wartezeit",
    "discount": "Online-Rabatt",
    "tour": "Erlebnis + Eintritt<br>ohne Wartezeit"
  },
  "noData": "Keine Daten verfügbar."
}
</i18n>

<style lang="scss">
@import "../css/variables";

.gap-column__event {
  margin-bottom: $gutter-sm;
}

.gap-column__headings-group {
  position: sticky;
  top: 0;
  z-index: 1;

  display: grid;
  grid-template-columns: $grid-columns-gap-xs;
  align-items: center;

  height: 3rem;

  font-size: 0.75em;
  text-align: center;
  color: $color-secondary-dark;

  background: #fff;
  border-bottom: 0.125rem solid $color-light;

  .is-inactive & {
    grid-template-columns: $grid-columns-gap-inactive;
    font-size: 0.7em;
  }

  @media (min-width: $breakpoint-gap-sm) {
    grid-template-columns: $grid-columns-gap-sm;
  }

  @media (min-width: $breakpoint-gap-md) {
    grid-template-columns: $grid-columns-gap-md;
  }
}

.gap-column__content {
  background: #fff;
}

.gap-column__discount {
  display: none;

  @media (min-width: $breakpoint-gap-md) {
    display: block;
  }
}

.gap-column__loading {
  text-align: center;
  padding: 1.25em;
}
</style>
